import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Check, ChevronRight, RotateCcw } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import { panelAccent } from 'theme/panelAccent';
import {
    statusChip,
    statusOf,
    canCheckIn,
    typeLabel,
    formatDateTime,
    PARTICIPANT_STATUS,
} from './participantTokens';

/**
 * Uma linha da lista de participantes.
 *
 * O nome vem primeiro e grande, com avatar — antes a primeira coluna era o
 * código (`c3999b`), um identificador técnico que ninguém usa para procurar
 * alguém. Código e item viram metadados secundários.
 *
 * `pendingUndo` liga o estado "acabou de entrar", em que a ação vira Desfazer
 * até a janela de cancelamento expirar.
 *
 * A linha inteira abre o detalhe; o check-in só acontece pelo botão. As ações
 * param a propagação para que validar alguém não abra a modal por cima da fila.
 * Para teclado, o nome é o botão de detalhe — a linha não pode ser um <button>
 * porque já contém outros.
 */
export default function ParticipantRow({
    participant,
    pendingUndo = false,
    undoSecondsLeft = 0,
    onCheckIn,
    onUndo,
    onDetail,
}) {
    const status = pendingUndo ? PARTICIPANT_STATUS.USED : statusOf(participant);
    const chip = statusChip[status];
    const checkinAllowed = canCheckIn(participant) && !pendingUndo;

    return (
        <Row $pending={pendingUndo} onClick={() => onDetail(participant)}>
            <Identity>
                <DynamicProfileImage
                    img={participant?.avatar}
                    word={participant?.name}
                    size='38px'
                    textSize='highlight'
                    bgColor={panelAccent(colors.secondaryPurple)}
                />

                <IdentityText>
                    <Name
                        type='button'
                        title={participant?.name}
                        aria-label={`Ver detalhes de ${participant?.name}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            onDetail(participant);
                        }}
                    >
                        {participant?.name}
                    </Name>
                    <Meta>
                        <MetaItem title={participant?.product}>{participant?.product}</MetaItem>
                        <Dot aria-hidden='true'>·</Dot>
                        <Code>{participant?.code}</Code>
                        <TypeTag>{typeLabel[participant?.type] || participant?.type}</TypeTag>
                    </Meta>
                </IdentityText>
            </Identity>

            <Trailing>
                <StatusArea>
                    <StatusChip $chip={chip}>
                        {status === PARTICIPANT_STATUS.USED && (
                            <Check size={11} strokeWidth={3} aria-hidden='true' />
                        )}
                        {chip.label}
                    </StatusChip>
                    <Purchased>{formatDateTime(participant?.created_at)}</Purchased>
                </StatusArea>

                <Actions onClick={(event) => event.stopPropagation()}>

                    {pendingUndo ? (
                        <UndoButton
                            type='button'
                            onClick={() => onUndo(participant)}
                            aria-label={`Desfazer check-in de ${participant?.name}`}
                        >
                            <RotateCcw size={14} strokeWidth={2.5} aria-hidden='true' />
                            Desfazer
                            <Countdown aria-hidden='true'>{undoSecondsLeft}s</Countdown>
                        </UndoButton>
                    ) : (
                        <CheckinButton
                            type='button'
                            disabled={!checkinAllowed}
                            onClick={() => checkinAllowed && onCheckIn(participant)}
                            aria-label={
                                checkinAllowed
                                    ? `Fazer check-in de ${participant?.name}`
                                    : `${participant?.name} não pode receber check-in`
                            }
                        >
                            <Check size={14} strokeWidth={3} aria-hidden='true' />
                            {participant?.type === 'ticket' ? 'Check-in' : 'Validar'}
                        </CheckinButton>
                    )}
                </Actions>

                <Chevron aria-hidden='true'>
                    <ChevronRight size={18} />
                </Chevron>
            </Trailing>
        </Row>
    );
}

/* ─────────────────────────────  Estilos  ───────────────────────────── */

const flash = keyframes`
    from { background: #F7EFFF; }
    to   { background: transparent; }
`;

/* Só affordance: diz que a linha abre algo. Fica fora do tab order porque o
   nome já é o alvo de teclado, e some no celular, onde a linha vira cartão. */
const Chevron = styled.span`
    display: none;
    color: ${colors.nightLight};
    transition: color 0.15s ease, transform 0.15s ease;

    @media screen and (min-width: ${breakpoints.smd}) {
        display: inline-flex;
        margin-left: -6px;
    }
`;

const Row = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
        background: #f9fafc;
        border-color: rgba(16, 24, 40, 0.09);
    }

    &:hover ${Chevron} {
        color: ${colors.nightDark};
        transform: translateX(2px);
    }

    ${({ $pending }) => $pending && `
        border-color: #DFC8F8;
        background: #FBF6FF;
    `}

    animation: ${({ $pending }) => ($pending ? flash : 'none')} 0.5s ease;

    @media screen and (min-width: ${breakpoints.smd}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }
`;

const Identity = styled.div`
    display: flex;
    align-items: center;
    gap: 11px;
    min-width: 0;
    flex: 1;
`;

const IdentityText = styled.div`
    min-width: 0;
`;

const Name = styled.button`
    display: block;
    max-width: 100%;
    margin: 0 0 2px;
    padding: 0;
    border: none;
    background: transparent;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

const Meta = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    font-size: 12px;
    color: ${colors.night};
`;

const MetaItem = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
`;

const Dot = styled.span`
    color: ${colors.nightLight};
    flex-shrink: 0;
`;

const Code = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    letter-spacing: 0.3px;
    flex-shrink: 0;
`;

/* Tipo vira rótulo neutro: antes competia visualmente com o status, porque
   ambos eram chips coloridos e saturados. */
const TypeTag = styled.span`
    flex-shrink: 0;
    margin-left: 3px;
    padding: 1px 7px;
    border-radius: 5px;
    background: ${colors.neutralBackground};
    border: 1px solid rgba(16, 24, 40, 0.08);
    font-size: 10px;
    font-weight: 600;
    color: ${colors.night};
`;

const Trailing = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-shrink: 0;

    @media screen and (min-width: ${breakpoints.smd}) {
        justify-content: flex-end;
    }
`;

const StatusArea = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;

    @media screen and (min-width: ${breakpoints.smd}) {
        align-items: flex-end;
        width: 150px;
    }
`;

const StatusChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 20px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: ${({ $chip }) => $chip.bg};
    color: ${({ $chip }) => $chip.text};
    border: 1px solid ${({ $chip }) => $chip.border};
`;

const Purchased = styled.span`
    font-size: 11px;
    color: ${colors.night};
    white-space: nowrap;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const CheckinButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 32px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid ${panelAccent(colors.secondaryPurple)};
    background: ${panelAccent(colors.secondaryPurple)};
    color: ${colors.neutralLight};
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: filter 0.15s ease;

    &:hover:not(:disabled) {
        filter: brightness(1.1);
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }

    /* Estado indisponível sem opacity+grayscale: o texto continua legível
       em vez de virar cinza sobre cinza. */
    &:disabled {
        cursor: not-allowed;
        background: ${colors.neutralLight};
        border-color: rgba(16, 24, 40, 0.12);
        color: #8a8a8a;
    }
`;

const UndoButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid #dfc8f8;
    background: ${colors.neutralLight};
    color: #6d14cc;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        background: #f7efff;
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

const Countdown = styled.span`
    min-width: 22px;
    padding: 1px 4px;
    border-radius: 5px;
    background: #f0e4fd;
    font-size: 10px;
    font-weight: 600;
    text-align: center;
`;
